import { debounce } from 'lodash';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { getReportData } from '../../../api/reports';
import { Layout, Pagination, Table } from '../../../common/components';
import NoReports from '../../../common/components/noReport/noReport';
import { useSetLoading } from '../../../common/hooks/appearance';
import { usePagination } from '../../../common/hooks/data';
import { reports, trustItemsHeaders } from '../../../utils/constant';
import * as handlers from '../../../utils/handlers';
import DownloadModal from '../../reports/downloadModal/downloadModal';
import EmailModal from '../../reports/emailModal/emailModal';
import ItemEmptyTile from '../../reports/item-empty-tile/item-empty-tile';
import Toolbar from '../../reports/toolbar/toolbar';
import TrustTypes from '../components/trustTypes/trustTypes';
import GenerateTrustItems from './generateTrustItems';
import TrustItemsReportRow from './trustItemRow';

function TrustItemsReport() {
  const [showTable, setShowTable] = useState({
    show: false,
    tableName: '',
    tableData: [],
    trustType: 0,
    needType: 0,
    totalResults: null,
    reportType: reports.TRUST_ITEMS_REPORT,
  });
  const [loading, setLoading] = useState(false);
  const [applyFilters, setApplyFilters] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const { page, limit, setPage, setLimit } = usePagination();

  const onDownloadClick = (response) => {
    handlers.successHandler({
      message: {
        message: response.msg,
        title: 'Success',
      },
    });
  };

  useEffect(() => {
    if (applyFilters) {
      generateTable('pagination');
    }
  }, [page, limit]);

  useEffect(() => {
    if (applyFilters && showTable?.show) {
      generateTable('click');
    }
  }, [showTable]);

  const generateTable = async (type, value = '') => {
    setApplyFilters(false);
    if (type !== 'pagination') {
      setPage(1);
      setLimit(5);
    }
    const requestParams = {
      trustType: showTable?.trustType,
      needType: showTable?.needType,
      page: type !== 'pagination' ? 1 : page,
      limit: type !== 'pagination' ? 5 : limit,
      ...((type === 'search' || searchText.length > 0) && {
        search: type === 'search' ? value : searchText,
      }),
    };

    if (!requestParams.search) {
      delete requestParams.search;
    }

    setLoading(true);
    try {
      const response = await getReportData(showTable.reportType, {
        ...requestParams,
      });
      setShowTable({
        ...showTable,
        show: true,
        tableData: response?.data?.result,
        totalResults: response?.data?.totalCount,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      handlers.apiErrorHandler(error);
    }
  };
  const toggleEmailModal = () => {
    if (showTable?.tableData?.length) {
      setIsEmailModalOpen(!isEmailModalOpen);
    }
  };
  const toggleDownloadModal = () => {
    if (showTable?.tableData?.length) {
      setIsDownloadModalOpen(!isDownloadModalOpen);
    }
  };
  const debounceApiCall = useCallback(
    debounce((val) => {
      generateTable('search', val);
    }, 2000),
    [],
  );
  const onSerchInputChange = (event) => {
    const {
      target: { value },
    } = event;
    setSearchText(value.trim());
    debounceApiCall(value);
  };

  const showLoading = useMemo(() => loading, [loading]);
  useSetLoading(showLoading);

  return (
    <Layout>
      <TrustTypes
        report="trustItems"
        setShowTable={setShowTable}
        setApplyFilters={setApplyFilters}
      />
      <GenerateTrustItems
        title="Trust Unconsumed Items List"
        description={`Generate a list of Items which are not consumed as on ${moment().format(
          'MMM DD, YYYY',
        )}`}
        generateTable={generateTable}
      />
      <hr />

      {showTable?.show ? (
        <>
          <div className="toolbar-style">
            <Toolbar
              title="Trust Unconsumed Items List"
              subTitle={`As on Date: ${moment().format('MMM DD, YYYY')}`}
              optionOneLabel="Email Report"
              optionOneIcon="MdOutlineEmail"
              onOptionOneClick={toggleEmailModal}
              optionTwoLabel="Download Report"
              optionTwoIcon="FiDownload"
              onOptionTwoClick={toggleDownloadModal}
              optionThreeLabel="Refresh"
              optionThreeIcon="RefreshIcon"
              onOptionThreeClick={() => {
                setApplyFilters(false);
                setPage(1);
                setLimit(5);
                generateTable('click');
              }}
              isTableDataPresent={showTable?.tableData?.length > 0}
              isSearchable
              setSearchText={setSearchText}
              searchText={searchText}
              onSerchInputChange={onSerchInputChange}
              SearchInputPlaceholder="Search with Contract Number"
            />
            {showTable?.tableData && showTable?.tableData?.length > 0 ? (
              <>
                <div className="table-resp-scroll">
                  <Table
                    headers={trustItemsHeaders}
                    bordered
                    responsive
                    className="dashboard-table pay-off-table"
                  >
                    <tbody>
                      {showTable?.tableData?.map((rowData, rowNumber) => (
                        <React.Fragment key={rowNumber}>
                          <TrustItemsReportRow id={rowNumber} data={rowData} />
                        </React.Fragment>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="pagination-araging-report">
                  <Pagination
                    totalResults={showTable?.totalResults}
                    page={page}
                    limit={limit}
                    setPage={(pageNo) => {
                      setApplyFilters(true);
                      setPage(pageNo);
                    }}
                    setLimit={(rowlimit) => {
                      setApplyFilters(true);
                      setLimit(rowlimit);
                    }}
                  />
                </div>
              </>
            ) : (
              <div className="noreports-trust">
                <ItemEmptyTile />
              </div>
            )}
          </div>

          <EmailModal
            isOpen={isEmailModalOpen}
            toggle={toggleEmailModal}
            reportType={reports?.TRUST_ITEMS_REPORT}
            apiValues={{
              needType: showTable?.needType || 0,
              trustType: showTable?.trustType || 0,
            }}
            isTrustItemsParams
          />
          <DownloadModal
            isOpen={isDownloadModalOpen}
            toggle={toggleDownloadModal}
            reportType={reports?.TRUST_ITEMS_REPORT}
            urlParams={{
              needType: showTable?.needType || 0,
              trustType: showTable?.trustType || 0,
            }}
            module="Trust"
            onDownloadClick={onDownloadClick}
            willReceiveReportFileInEmail
          />
        </>
      ) : (
        <div className="noreports-trust">
          <NoReports />
        </div>
      )}
    </Layout>
  );
}

export default memo(TrustItemsReport);
